import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import {
  getJiraAuthUrl,
  disconnectJira,
  getJiraProjects,
  saveJiraSelectedProjects,
  importJiraTasks,
  subscribeToJiraStatus,
} from '../../../services/jira';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './QuickBooksCard.module.scss';
import jiraStyles from './JiraCard.module.scss';

const JIRA_CONNECTED_PARAM = 'jira_connected';
const JIRA_ERROR_PARAM = 'jira_error';

const ERROR_MESSAGES = {
  access_denied: 'Jira authorization was cancelled.',
  missing_params: 'Authorization failed: missing parameters from Atlassian.',
  invalid_state: 'Authorization failed: invalid or expired session state.',
  state_expired: 'Authorization session expired. Please try again.',
  server_error:
    'A server error occurred during authorization. Please try again.',
  no_sites: 'No accessible Jira sites found for this account.',
};

function fmtDate(dateObj) {
  if (!dateObj) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
}

function fmtNumber(n) {
  if (n == null) return '—';
  return n.toLocaleString();
}

export const JiraCard = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const location = useLocation();
  const history = useHistory();

  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const isAdmin = userProfile?.activeCompany?.role === 'Admin';

  const [jiraStatus, setJiraStatus] = useState({
    connected: false,
    cloudId: null,
    siteName: null,
    siteUrl: null,
    selectedProjects: [],
    importStatus: null,
    lastImportAt: null,
    lastImportStats: null,
    connectedAt: null,
    connectedBy: null,
  });

  // Project selection state
  const [allProjects, setAllProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null); // null = not in edit mode
  const [savingProjects, setSavingProjects] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);

  // Connect/disconnect
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // ── OAuth redirect handling ─────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get(JIRA_CONNECTED_PARAM);
    const errorCode = params.get(JIRA_ERROR_PARAM);
    if (!connected && !errorCode) return;

    if (connected === 'true') {
      addNotification({
        message: 'Jira connected successfully.',
        level: notificationStates.success,
      });
    } else if (errorCode) {
      addNotification({
        message: ERROR_MESSAGES[errorCode] || 'Jira authorization failed.',
        level: notificationStates.error,
      });
    }

    params.delete(JIRA_CONNECTED_PARAM);
    params.delete(JIRA_ERROR_PARAM);
    const cleanSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: cleanSearch ? `?${cleanSearch}` : '',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Firestore real-time status ──────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeToJiraStatus(companyId, setJiraStatus);
    return unsub;
  }, [companyId]);

  // ── When a running import finishes, clear local importing flag ──────────────
  useEffect(() => {
    if (
      importing &&
      jiraStatus.importStatus &&
      !jiraStatus.importStatus.running
    ) {
      setImporting(false);
    }
  }, [jiraStatus.importStatus, importing]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleConnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can connect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setConnecting(true);
    try {
      const { authUrl } = await getJiraAuthUrl(companyId, userId);
      window.location.href = authUrl;
    } catch {
      addNotification({
        message: 'Failed to initiate Jira authorization. Please try again.',
        level: notificationStates.error,
      });
      setConnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleDisconnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can disconnect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectJira(companyId, userId);
      setPendingSelection(null);
      addNotification({
        message: 'Jira disconnected.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to disconnect Jira. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleEditProjects = useCallback(async () => {
    setProjectsLoading(true);
    setPendingSelection(jiraStatus.selectedProjects.map((p) => p.id));
    try {
      const { projects } = await getJiraProjects(companyId);
      setAllProjects(projects || []);
    } catch {
      addNotification({
        message: 'Failed to load Jira projects. Please try again.',
        level: notificationStates.error,
      });
      setPendingSelection(null);
    } finally {
      setProjectsLoading(false);
    }
  }, [companyId, jiraStatus.selectedProjects]);

  const handleToggleProject = useCallback((projectId) => {
    setPendingSelection((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  }, []);

  const handleSaveProjects = useCallback(async () => {
    if (!pendingSelection) return;
    setSavingProjects(true);
    try {
      const selected = allProjects.filter((p) =>
        pendingSelection.includes(p.id)
      );
      await saveJiraSelectedProjects(companyId, selected);
      setPendingSelection(null);
      addNotification({
        message: `${selected.length} project${selected.length !== 1 ? 's' : ''} saved.`,
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to save project selection. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSavingProjects(false);
    }
  }, [companyId, pendingSelection, allProjects]);

  const handleImport = useCallback(async () => {
    setImporting(true);
    try {
      const result = await importJiraTasks(companyId);
      addNotification({
        message: `Import complete. ${fmtNumber(result.imported)} tasks imported.`,
        level: notificationStates.success,
      });
    } catch (err) {
      addNotification({
        message:
          err?.response?.data?.message || 'Import failed. Please try again.',
        level: notificationStates.error,
      });
      setImporting(false);
    }
    // importing flag cleared by the useEffect watching importStatus.running
  }, [companyId]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const isImportRunning =
    importing || jiraStatus.importStatus?.running === true;
  const importProgress = jiraStatus.importStatus?.imported ?? 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Connection status panel */}
      <div className={styles.statusPanel}>
        <div className={styles.statusLeft}>
          <span
            className={`${styles.badge} ${jiraStatus.connected ? styles.connected : styles.disconnected}`}
          >
            {jiraStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          {jiraStatus.connected ? (
            <span className={styles.connectedMeta}>
              {jiraStatus.siteName && `Site: ${jiraStatus.siteName}`}
              {jiraStatus.connectedAt &&
                ` · Connected ${fmtDate(jiraStatus.connectedAt)}`}
            </span>
          ) : (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Connect your Atlassian account to import Jira tasks into Prodaff.
            </Text>
          )}
        </div>
        {isAdmin &&
          (jiraStatus.connected ? (
            <Button
              text={disconnecting ? 'Disconnecting…' : 'Disconnect'}
              variant={buttonVariants.secondary}
              size={buttonSizes.sm}
              onClick={handleDisconnect}
              disabled={disconnecting}
            />
          ) : (
            <Button
              text={connecting ? 'Redirecting…' : 'Connect Jira'}
              variant={buttonVariants.primary}
              size={buttonSizes.sm}
              onClick={handleConnect}
              disabled={connecting}
            />
          ))}
      </div>

      {/* Project selection panel */}
      {jiraStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Projects</span>
              <span className={styles.sectionSubtitle}>
                Choose which Jira projects to import tasks from.
              </span>
            </div>
            <div className={styles.sectionActions}>
              {pendingSelection === null ? (
                <Button
                  text="Edit Selection"
                  variant={buttonVariants.secondary}
                  size={buttonSizes.sm}
                  onClick={handleEditProjects}
                  disabled={projectsLoading}
                />
              ) : (
                <>
                  <Button
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    size={buttonSizes.sm}
                    onClick={() => setPendingSelection(null)}
                    disabled={savingProjects}
                  />
                  <Button
                    text={savingProjects ? 'Saving…' : 'Save Selection'}
                    variant={buttonVariants.primary}
                    size={buttonSizes.sm}
                    onClick={handleSaveProjects}
                    disabled={savingProjects || pendingSelection.length === 0}
                  />
                </>
              )}
            </div>
          </div>

          <div className={styles.sectionBody}>
            {pendingSelection === null ? (
              // Read-only view of saved projects
              jiraStatus.selectedProjects.length === 0 ? (
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Normal}
                  color="gray-500"
                >
                  No projects selected yet. Click "Edit Selection" to choose
                  projects.
                </Text>
              ) : (
                <div className={jiraStyles.projectChips}>
                  {jiraStatus.selectedProjects.map((p) => (
                    <span key={p.id} className={jiraStyles.projectChip}>
                      <span className={jiraStyles.projectKey}>{p.key}</span>
                      {p.name}
                    </span>
                  ))}
                </div>
              )
            ) : projectsLoading ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                Loading projects…
              </Text>
            ) : (
              // Editable checkbox list
              <div className={jiraStyles.projectList}>
                {allProjects.length === 0 ? (
                  <EmptyState
                    illustration
                    title="No Projects Found"
                    description="No projects found in this Jira site. Reconnect or check your Jira permissions."
                  />
                ) : (
                  <>
                    <div className={jiraStyles.selectAllRow}>
                      <button
                        className={jiraStyles.selectAllBtn}
                        onClick={() =>
                          setPendingSelection(
                            pendingSelection.length === allProjects.length
                              ? []
                              : allProjects.map((p) => p.id)
                          )
                        }
                      >
                        {pendingSelection.length === allProjects.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                      <span className={jiraStyles.selectionCount}>
                        {pendingSelection.length} of {allProjects.length}{' '}
                        selected
                      </span>
                    </div>
                    <div className={jiraStyles.checkboxGrid}>
                      {allProjects.map((project) => {
                        const checked = pendingSelection.includes(project.id);
                        return (
                          <label
                            key={project.id}
                            className={`${jiraStyles.projectRow} ${checked ? jiraStyles.checked : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggleProject(project.id)}
                            />
                            <div className={jiraStyles.projectInfo}>
                              <span className={jiraStyles.projectKey}>
                                {project.key}
                              </span>
                              <span className={jiraStyles.projectName}>
                                {project.name}
                              </span>
                              {project.type && (
                                <span className={jiraStyles.projectType}>
                                  {project.type}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import panel */}
      {jiraStatus.connected && jiraStatus.selectedProjects.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Import Tasks</span>
              <span className={styles.sectionSubtitle}>
                {jiraStatus.lastImportAt
                  ? `Last imported ${fmtDate(jiraStatus.lastImportAt)} · Re-import fetches only updated issues.`
                  : 'Pull all tasks from selected projects into Prodaff.'}
              </span>
            </div>
            <div className={styles.sectionActions}>
              <Button
                text={
                  isImportRunning
                    ? 'Importing…'
                    : jiraStatus.lastImportAt
                      ? 'Re-import'
                      : 'Import Now'
                }
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleImport}
                disabled={isImportRunning}
              />
            </div>
          </div>

          <div className={styles.sectionBody}>
            {/* Live progress bar */}
            {isImportRunning && (
              <div className={jiraStyles.progressBlock}>
                <div className={jiraStyles.progressLabel}>
                  Importing tasks…{' '}
                  {importProgress > 0 && (
                    <span className={jiraStyles.progressCount}>
                      {fmtNumber(importProgress)} imported so far
                    </span>
                  )}
                </div>
                <div className={jiraStyles.progressBarTrack}>
                  <div className={jiraStyles.progressBarFill} />
                </div>
              </div>
            )}

            {/* Last import stats */}
            {!isImportRunning && jiraStatus.lastImportStats && (
              <div className={styles.syncStats}>
                <div className={styles.syncStatsTitle}>
                  Last import · {fmtDate(jiraStatus.lastImportAt)}
                </div>
                <div className={styles.syncStatsGrid}>
                  <div className={styles.syncStatItem}>
                    <span className={styles.statLabel}>Imported</span>
                    <span className={`${styles.statValue} ${styles.success}`}>
                      {fmtNumber(jiraStatus.lastImportStats.imported)}
                    </span>
                  </div>
                  <div className={styles.syncStatItem}>
                    <span className={styles.statLabel}>Projects</span>
                    <span className={styles.statValue}>
                      {jiraStatus.lastImportStats.projectCount ?? '—'}
                    </span>
                  </div>
                  <div className={styles.syncStatItem}>
                    <span className={styles.statLabel}>Errors</span>
                    <span
                      className={`${styles.statValue} ${(jiraStatus.lastImportStats.errors ?? 0) > 0 ? styles.error : ''}`}
                    >
                      {fmtNumber(jiraStatus.lastImportStats.errors ?? 0)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {!isImportRunning && !jiraStatus.lastImportStats && (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                No imports yet. Click "Import Now" to pull tasks from the
                selected projects.
              </Text>
            )}
          </div>
        </div>
      )}
    </>
  );
};
